"use client";

import { useState, useEffect } from "react";
import { createStorefrontApiClient } from "@shopify/storefront-api-client";
import { toast } from "sonner";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ButtonMaster } from "@/components/Atoms/Buttons";
import { MultiPurposeParagraph } from "@/components/Atoms/Paragraph";
import styles from "./index.module.scss";

interface ShopifySimpleBuyButtonProps {
  domain: string;
  storefrontAccessToken: string;
  productId?: string;
  productHandle?: string;
  variantId?: string;
  maxQuantity?: number;
  label?: string;
  color?: any;
  cta?: string;
  type?: any;
  customColor?: any;
  config: any;
  cart: any;
}

export default function ShopifySimpleBuyButton({
  domain,
  storefrontAccessToken,
  productId,
  productHandle,
  variantId,
  maxQuantity = 5,
  label,
  color,
  cta,
  type,
  customColor,
  config,
  cart,
}: ShopifySimpleBuyButtonProps) {
  const [product, setProduct] = useState<any>(null);
  const [selectedVariant, setSelectedVariant] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [quantity, setQuantity] = useState(1);


  // Format product ID if needed
  const formattedProductId = productId?.startsWith("gid://")
    ? productId
    : `gid://shopify/Product/${productId}`;

  // Initialize Shopify client
  const getClient = () => {
    // Make sure domain is properly formatted
    let formattedDomain = domain;
    if (!formattedDomain.startsWith("http")) {
      formattedDomain = `https://${formattedDomain}`;
    }

    try {
      return createStorefrontApiClient({
        storeDomain: formattedDomain,
        apiVersion: "2024-10",
        publicAccessToken: storefrontAccessToken,
      });
    } catch (error) {
      console.error("Error initializing Shopify client:", error);
      toast.error(
        "Failed to initialize Shopify client. Check your domain and access token."
      );
      return null;
    }
  };

  // Fetch product data
  useEffect(() => {
    const fetchProduct = async () => {
      setIsLoading(true);

      try {
        const client = getClient();
        if (!client) {
          throw new Error("Failed to initialize Shopify client");
        }

        let query = "";
        let variables = {};

        if (productHandle) {
          query = `
            query GetProductByHandle($handle: String!) {
              product(handle: $handle) {
                id
                title
                variants(first: 10) {
                  edges {
                    node {
                      id
                      title
                      price {
                        amount
                        currencyCode
                      }
                      availableForSale
                    }
                  }
                }
              }
            }
          `;
          variables = { handle: productHandle };
        } else if (formattedProductId) {
          query = `
            query GetProductById($id: ID!) {
              product(id: $id) {
                id
                title
                variants(first: 10) {
                  edges {
                    node {
                      id
                      title
                      price {
                        amount
                        currencyCode
                      }
                      availableForSale
                    }
                  }
                }
              }
            }
          `;
          variables = { id: formattedProductId };
        } else {
          throw new Error("Either productId or productHandle must be provided");
        }

        const { data, errors } = await client.request(query, {
          variables,
        });

        if (errors) {
          console.error("GraphQL errors:", JSON.stringify(errors, null, 2));
          throw new Error(
            `Failed to fetch product: ${
              errors.message || JSON.stringify(errors)
            }`
          );
        }

        if (data?.product) {
          setProduct(data.product);

          // Set the selected variant
          const variants = data.product.variants.edges.map(
            (edge: any) => edge.node
          );

          if (variantId) {
            const variant = variants.find((v: any) => v.id === variantId);
            setSelectedVariant(variant || variants[0]);
          } else {
            setSelectedVariant(variants[0]);
          }
        } else {
          throw new Error("Product not found");
        }
      } catch (error: any) {
        console.error("Error fetching product:", error);
        toast.error(error.message || "Failed to load product information");
      } finally {
        setIsLoading(false);
      }
    };

    if (
      domain &&
      storefrontAccessToken &&
      (formattedProductId || productHandle)
    ) {
      fetchProduct();
    } else {
      console.error("Missing required props:", {
        domain,
        storefrontAccessToken,
        productId: formattedProductId,
        productHandle,
      });
      setIsLoading(false);
    }
  }, [
    domain,
    storefrontAccessToken,
    formattedProductId,
    productHandle,
    variantId,
  ]);

  // Create or retrieve cart
  const getCart = async () => {
    try {
      const client = getClient();
      if (!client) {
        throw new Error("Failed to initialize Shopify client");
      }

      // Check if we already have a cart ID in localStorage
      const existingCartId = localStorage.getItem("shopifyCartId");

      if (existingCartId) {
        // Verify the cart still exists
        const checkCartQuery = `
          query GetCart($cartId: ID!) {
            cart(id: $cartId) {
              id
            }
          }
        `;

        const { data, errors } = await client.request(checkCartQuery, {
          variables: { cartId: existingCartId },
        });

        if (!errors && data?.cart) {
          return existingCartId;
        }
      }

      // Create a new cart
      const createCartMutation = `
        mutation CreateCart {
          cartCreate {
            cart {
              id
              checkoutUrl
            }
            userErrors {
              field
              message
            }
          }
        }
      `;

      const { data, errors } = await client.request(createCartMutation);

      if (errors || data?.cartCreate?.userErrors?.length > 0) {
        throw new Error("Failed to create cart");
      }

      const newCartId = data?.cartCreate?.cart?.id;

      if (newCartId) {
        localStorage.setItem("shopifyCartId", newCartId);
        return newCartId;
      }

      throw new Error("Failed to create cart");
    } catch (error) {
      console.error("Error creating/retrieving cart:", error);
      throw error;
    }
  };

  // Add to cart function
  const addToCart = async () => {
    if (!selectedVariant) return;

    setIsAddingToCart(true);

    try {
      const cartId = await getCart();
      const client = getClient();
      if (!client) {
        throw new Error("Failed to initialize Shopify client");
      }

      const addToCartMutation = `
        mutation AddToCart($cartId: ID!, $lines: [CartLineInput!]!) {
          cartLinesAdd(cartId: $cartId, lines: $lines) {
            cart {
              id
              checkoutUrl
              totalQuantity
            }
            userErrors {
              field
              message
            }
          }
        }
      `;

      const { data, errors } = await client.request(addToCartMutation, {
        variables: {
          cartId,
          lines: [
            {
              quantity: quantity,
              merchandiseId: selectedVariant.id,
            },
          ],
        },
      });

      if (errors || data?.cartLinesAdd?.userErrors?.length > 0) {
        throw new Error("Failed to add item to cart");
      }

      // Success!
      toast.success(
        `${product.title} (${quantity}) has been added to your cart.`
      );

      // Dispatch a custom event to notify the mini cart
      window.dispatchEvent(new CustomEvent("shopify:cart:updated"));
    } catch (error) {
      console.error("Error adding to cart:", error);
      toast.error("Failed to add item to cart");
    } finally {
      setIsAddingToCart(false);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-4 space-y-4">
          <Skeleton className="h-6 w-1/3" />
          <Skeleton className="h-10 w-full" />
        </CardContent>
        <CardFooter>
          <Skeleton className="h-10 w-full" />
        </CardFooter>
      </Card>
    );
  }

  if (!product || !selectedVariant) {
    return (
      <Card>
        <CardContent className="p-4">
          <p className="text-center text-muted-foreground">Product not found</p>
        </CardContent>
      </Card>
    );
  }

  const formatPrice = (amount: string, currencyCode: string) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode,
    }).format(Number.parseFloat(amount));
  };

  const price = formatPrice(
    selectedVariant.price.amount,
    selectedVariant.price.currencyCode
  );
  const totalPrice = formatPrice(
    (Number.parseFloat(selectedVariant.price.amount) * quantity).toString(),
    selectedVariant.price.currencyCode
  );

  const style = {
    "--highlightColor": config?.highlightColor?.color.hex,
    "--color": config?.textColor.color.hex,
    "--bgColor": cart?.backgroundColor.color.hex,
  } as React.CSSProperties;

  return (
    <Card className="flex flex-row-reverse  justify-between gap-1 items-stretch h-full">
      <CardContent className="h-full">
        <div
          style={style}
          className={`flex items-center gap-2 h-full ${styles.selectContent}`}
        >
          <Select
            value={quantity.toString()}
            onValueChange={(value: any) => setQuantity(Number.parseInt(value))}
          >
            <SelectTrigger id="quantity">
              <SelectValue placeholder="1" />
            </SelectTrigger>

            <SelectContent>
              {Array.from({ length: maxQuantity }, (_, i) => i + 1).map(
                (num) => (
                  <SelectItem
                    style={style}
                    className={` ${styles.selectContent}`}
                    key={num}
                    value={num.toString()}
                  >
                    <MultiPurposeParagraph
                      type="default"
                      data={`${num}`}
                      color={cart?.backgroundColor.color}
                    />
                  </SelectItem>
                )
              )}
            </SelectContent>
          </Select>

          {/*    {quantity > 1 && (
            <p className="text-sm text-muted-foreground">Total: {totalPrice}</p>
          )} */}
        </div>
      </CardContent>
      <CardFooter>
        <ButtonMaster
          onClick={addToCart}
          anim={"slideLeft"}
          type={type}
          color={color}
          cta={cta}
          customColor={customColor}
        >
          {isLoading ? (
            <Skeleton className="h-4 w-[100px]" />
          ) : isAddingToCart ? (
            "Adding..."
          ) : (
            <>
              {" "}
              <p className="">
                {" "}
                {label} {quantity > 1 ? `${totalPrice}` : `${price}`}{" "}
              </p>
            </>
          )}
        </ButtonMaster>
      </CardFooter>
    </Card>
  );
}
